import { useState, useLayoutEffect } from 'react'

/**
 * Computes fixed-position CSS (top/left/width) for a dropdown anchored below
 * a ref'd element, so it isn't clipped by a modal's overflow:auto body.
 * Recomputes on scroll/resize while open.
 *
 * @param {import('react').RefObject<HTMLElement>} anchorRef
 * @param {boolean} open
 * @param {number} [gap] — px gap between anchor and dropdown
 * @returns {object} inline style to spread onto the dropdown element
 */
export function useFixedDropdownPosition(anchorRef, open, gap = 6) {
  const [style, setStyle] = useState({})

  useLayoutEffect(() => {
    if (!open) return

    function update() {
      if (!anchorRef.current) return
      const rect = anchorRef.current.getBoundingClientRect()
      setStyle({ position: 'fixed', top: rect.bottom + gap, left: rect.left, width: rect.width })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, anchorRef, gap])

  return style
}
